import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, WritableSignal, inject, signal } from '@angular/core';
import { Observable, catchError, finalize, map, of, throwError } from 'rxjs';
import { SPECIALISMS_MAX_SELECTED } from './specialisms-limits.type';
import { ErrorService, NormalisedHttpError } from '../../error/error.service';
import { LoadingObject, LoadingService } from '../../loaders';

type SpecialismSelectionApiResponse = {
  data: {
    selected_topic_ids: number[];
  };
};

type SpecialismSelectionRequest = {
  topic_ids: number[];
};

type normalisedSpecialismError = NormalisedHttpError;

@Injectable({
  providedIn: 'root',
})
export class ProfessionalSpecialismsService {
  private readonly http = inject(HttpClient);
  private readonly errorService = inject(ErrorService);

  private readonly _selectedTopicIds = signal<number[]>([]);
  private readonly _pendingTopicIds = signal<number[]>([]);
  
  private readonly _error = signal<string | null>(null);

  private _loadingObject = signal<LoadingObject>({});  // In the legacy application, loadingService caused a circular dependency error.
  private loadingService = inject(LoadingService);

  specialismsLoaded = false;


  get loadingObject(): LoadingObject {
      return this._loadingObject();
  }
  public get loadingObjectSignal() : WritableSignal<LoadingObject> {
      return this._loadingObject;
  }
  isAnythingLoading(): boolean {
      return this.loadingService.isAnythingLoading(this._loadingObject());
  }

  setLoadingStatus(key: string, status: boolean): void {
      this._loadingObject.set(this.loadingService.setLoadingStatus(key, status, this._loadingObject()));
  }
  readonly selectedTopicIds = this._selectedTopicIds.asReadonly();
  readonly pendingTopicIds = this._pendingTopicIds.asReadonly();
  readonly error = this._error.asReadonly();

  loadSpecialisms(freshFromServer = false): Observable<void> {
    if (!freshFromServer && this.specialismsLoaded) {
      return of(void 0);
    }

    this.setLoadingStatus('userSpecialisms', true);
    this._error.set(null);

    return this.http.get<SpecialismSelectionApiResponse>('api/v1/user/specialisms').pipe(
      map((selection) => {
        this._selectedTopicIds.set(this.normaliseIds(selection?.data?.selected_topic_ids ?? []));
        this.specialismsLoaded = true;
      }),
      catchError((errorResponse: HttpErrorResponse) => {
        const normalisedError = this.errorService.normaliseHttpError(errorResponse, 'profile.specialisms_load_failed');
        this._error.set(normalisedError.message);
        return throwError(() => normalisedError);
      }),
      finalize(() => this.setLoadingStatus('userSpecialisms', false)),
    );
  }

  toggleTopic(topicId: number): Observable<void> {
    const currentSelectedTopicIds = [...this._selectedTopicIds()];
    const currentPendingTopicIds = [...this._pendingTopicIds()];

    if (currentPendingTopicIds.includes(topicId)) {
      return of(void 0);
    }

    const isAlreadySelected = currentSelectedTopicIds.includes(topicId);
    const nextSelectedTopicIds = isAlreadySelected
      ? currentSelectedTopicIds.filter((selectedTopicId) => selectedTopicId !== topicId)
      : [...currentSelectedTopicIds, topicId];

    if (!isAlreadySelected && currentSelectedTopicIds.length >= SPECIALISMS_MAX_SELECTED) {
      const normalisedError: normalisedSpecialismError = {
        message: 'profile.specialisms_limit_reached',
        status: 422,
      };
      this._error.set(normalisedError.message);
      return throwError(() => normalisedError);
    }

    this._pendingTopicIds.set([...currentPendingTopicIds, topicId]);
    this._selectedTopicIds.set(nextSelectedTopicIds);
    this._error.set(null);

    this.setLoadingStatus('userSelectedSpecialisms', true);

    return this.http.put<SpecialismSelectionApiResponse>('api/v1/user/specialisms', {
      topic_ids: nextSelectedTopicIds,
    } as SpecialismSelectionRequest).pipe(
      map((response) => {
        const selectedTopicIds = this.normaliseIds(response?.data?.selected_topic_ids ?? nextSelectedTopicIds);
        this._selectedTopicIds.set(selectedTopicIds);
      }),
      catchError((errorResponse: HttpErrorResponse) => {
        this._selectedTopicIds.set(currentSelectedTopicIds);
        const normalisedError = this.errorService.normaliseHttpError(errorResponse, 'profile.specialisms_save_failed');
        this._error.set(normalisedError.message);
        return throwError(() => normalisedError);
      }),
      finalize(() => {
        this._pendingTopicIds.set(this._pendingTopicIds().filter((pendingTopicId) => pendingTopicId !== topicId));
        this.setLoadingStatus('userSelectedSpecialisms', false);
      }),
    );
  }

  private normaliseIds(ids: number[]): number[] {
    return Array.from(new Set(ids.filter((value) => Number.isFinite(value))));
  }
}
